import React from 'react';
import { MotionButton, FadeInSection } from '../ui/MotionPrimitives';

type ListingType = 'all' | 'sale' | 'rent';
type PropertyType = 'all' | 'apartments' | 'villas' | 'plots' | 'hotels' | 'projects_offplan';

interface PropertiesHeroProps {
  listingType: ListingType;
  onListingTypeChange: (value: ListingType) => void;
  propertyType: PropertyType;
  onPropertyTypeChange: (value: PropertyType) => void;
}

const PropertiesHero: React.FC<PropertiesHeroProps> = ({
  listingType,
  onListingTypeChange,
  propertyType,
  onPropertyTypeChange,
}) => {
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
  };

  return (
    <FadeInSection className="w-full bg-white pt-0 pb-0 sm:pt-32 sm:pb-12">
      <div className="mx-auto max-w-6xl px-0 sm:px-6 md:px-8">
        <div className="relative overflow-hidden rounded-none sm:rounded-3xl bg-gradient-to-r from-sky-200 to-sky-400 min-h-screen sm:min-h-0">
          <img
            src="/images/pics/propertiesimghero.jpg"
            alt="Premium property listings"
            className="h-full min-h-screen w-full object-cover object-center sm:min-h-0 sm:h-[320px] md:h-[400px] lg:h-[440px]"
          />

          {/* Search bar overlay */}
          <div className="pointer-events-none absolute inset-x-3 bottom-3 sm:inset-x-4 sm:bottom-4 md:inset-x-8 md:bottom-6">
            <form
              onSubmit={handleSubmit}
              className="pointer-events-auto mx-auto flex w-full max-w-md flex-col gap-3 rounded-2xl border border-white/70 bg-white/95 p-3 shadow-xl backdrop-blur-sm sm:max-w-2xl sm:p-4 md:max-w-4xl md:flex-row md:flex-nowrap md:items-end md:gap-4 md:p-5"
            >
              {/* Property Type */}
              <div className="w-full md:w-[23%]">
                <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-black">
                  Property Type
                </label>
                <select
                  className="mt-2 w-full rounded-full border border-gray-300 bg-white px-4 py-2 text-sm text-black outline-none transition focus:border-[#D88546] focus:ring-2 focus:ring-[#D88546]/40"
                  value={propertyType}
                  onChange={(e) =>
                    onPropertyTypeChange(e.target.value as PropertyType)
                  }
                >
                  <option value="all">All Types</option>
                  <option value="apartments">Apartments</option>
                  <option value="villas">Villas</option>
                  <option value="plots">Plots</option>
                  <option value="hotels">Hotels</option>
                  <option value="projects_offplan">Projects offplan</option>
                </select>
              </div>

              {/* Location */}
              <div className="w-full md:w-[23%]">
                <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-black">
                  Location
                </label>
                <input
                  type="text"
                  placeholder="City or community"
                  className="mt-2 w-full rounded-full border border-gray-300 bg-white px-4 py-2 text-sm text-black outline-none transition placeholder:text-gray-400 focus:border-[#D88546] focus:ring-2 focus:ring-[#D88546]/40"
                />
              </div>

              {/* Price Min / Max */}
              <div className="flex w-full gap-3 md:w-[31%]">
                <div className="w-1/2">
                  <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-black">
                    Price (Min)
                  </label>
                  <input
                    type="number"
                    min={0}
                    placeholder="0"
                    className="mt-2 w-full rounded-full border border-gray-300 bg-white px-4 py-2 text-sm text-black outline-none transition placeholder:text-gray-400 focus:border-[#D88546] focus:ring-2 focus:ring-[#D88546]/40"
                  />
                </div>
                <div className="w-1/2">
                  <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-black">
                    Price (Max)
                  </label>
                  <input
                    type="number"
                    min={0}
                    placeholder="Any"
                    className="mt-2 w-full rounded-full border border-gray-300 bg-white px-4 py-2 text-sm text-black outline-none transition placeholder:text-gray-400 focus:border-[#D88546] focus:ring-2 focus:ring-[#D88546]/40"
                  />
                </div>
              </div>

              {/* Listing type toggle */}
              <div className="w-full md:w-[23%]">
                <span className="block text-xs font-semibold uppercase tracking-[0.16em] text-black">
                  Listing Type
                </span>
                <div className="mt-2 inline-flex w-full rounded-full bg-gray-100 p-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-black sm:text-xs">
                  {(
                    [
                      { key: 'all' as const, label: 'All' },
                      { key: 'sale' as const, label: 'For Sale' },
                      { key: 'rent' as const, label: 'For Rent' },
                    ]
                  ).map((option) => (
                    <MotionButton
                      key={option.key}
                      type="button"
                      onClick={() => onListingTypeChange(option.key)}
                      className={`flex-1 rounded-full px-2 py-1 whitespace-nowrap transition ${
                        listingType === option.key
                          ? 'bg-[#D88546] text-black shadow-sm'
                          : 'bg-transparent text-black/70 hover:bg-white'
                      }`}
                    >
                      {option.label}
                    </MotionButton>
                  ))}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </FadeInSection>
  );
};

export default PropertiesHero;
